import { api_json, ApiError } from './api'
import { avatar_html } from './avatar'
import { icon, type IconName } from './icons'
import { MOTHER_SYSTEMS, PRODUCT } from './platform'
import {
  get_theme_preference,
  get_ui_density_preference,
  next_theme_preference,
  next_ui_density_preference,
  set_theme_preference,
  set_ui_density_preference,
  theme_preference_icon,
  theme_preference_label,
  ui_density_preference_icon,
  ui_density_preference_label,
  type ThemePreference,
  type UiDensityPreference
} from './theme'

type NavItem = {
  href: string
  label: string
  icon: IconName
  description?: string
  acl_tag?: string
  /** Entitlement de módulo (ex.: M20) quando o href não é /modules/mXX. */
  module_code?: string
  /** Item restrito ao admin de plataforma (`is_platform_root` do /me — decisão do backend). */
  platform_admin?: boolean
}

type NavCategory = {
  id: string
  label: string
  icon: IconName
  description?: string
  collapsible?: boolean
  default_collapsed?: boolean
  items: NavItem[]
}

type UserShortcutItem = { href: string; label: string; icon: IconName; external?: boolean }

type ShellMenuPreferences = {
  favorite_keys: string[]
  collapsed_categories: string[]
}

type ShellAuthUser = {
  email?: string
  name?: string | null
  role?: string
  avatar_url?: string | null
  acl_tags?: string[]
  entitlements?: string[]
  /** Bibliotecas de requisitos ativas na org (F5). */
  library_entitlements?: string[]
  is_platform_root?: boolean
}

/** Menu SST (NR-01) — labels e módulos do vertical de riscos ocupacionais. */
const NAV_SST: NavCategory[] = [
  {
    id: 'principal',
    label: 'Principal',
    icon: 'dashboard',
    collapsible: true,
    items: [
      { href: '/', label: 'Dashboard', icon: 'dashboard', description: 'Resumo operacional' },
      { href: '/conformidade', label: 'Conformidade', icon: 'book', description: 'Painel e biblioteca de requisitos', acl_tag: 'biblioteca:read' },
      { href: '/gap-analysis', label: 'Gap analysis', icon: 'search', description: 'Questionário e plano de ação', acl_tag: 'biblioteca:write' },
      { href: '/onboarding', label: 'Primeiros passos', icon: 'play', description: 'Ativação do trial', acl_tag: 'onboarding:read' },
      { href: '/admin/users', label: 'Usuários', icon: 'user', description: 'Contas e permissões', acl_tag: 'users:list' },
      { href: '/admin/acl', label: 'Permissões', icon: 'key', description: 'Grupos e ACL', acl_tag: 'acl:manage' },
      { href: '/admin/platform', label: 'Plataforma', icon: 'settings', description: 'Organizações e planos', platform_admin: true },
      { href: '/admin/integrations', label: 'Integrações', icon: 'settings', description: 'LLM, RAG e chaves de API', platform_admin: true }
    ]
  },
  {
    id: 'base-operacional',
    label: 'Base operacional',
    icon: 'hard-hat',
    collapsible: true,
    items: [
      { href: '/modules/m01', label: 'Core e configuração', icon: 'settings', description: 'Cadastros estruturais e RBAC' },
      { href: '/modules/m02', label: 'PGR e riscos', icon: 'alert-triangle', description: 'Inventário de riscos' },
      { href: '/modules/m03', label: 'Plano de ação', icon: 'clipboard-check', description: 'Ações vinculadas por origem' },
      { href: '/modules/m04', label: 'Treinamentos', icon: 'book', description: 'Matriz e execução' },
      { href: '/modules/m05', label: 'Ordens de serviço', icon: 'file-text', description: 'Versionamento e ciência' },
      { href: '/modules/m06', label: 'Acidentes', icon: 'alert-triangle', description: 'Registro e tratativas' },
      { href: '/modules/m07', label: 'Emergências', icon: 'bell', description: 'Planos e simulados' }
    ]
  },
  {
    id: 'ssot-operacional',
    label: 'SSOT operacional',
    icon: 'clipboard-check',
    collapsible: true,
    items: [
      { href: '/modules/m08', label: 'Terceiros', icon: 'user', description: 'Conformidade de contratadas' },
      { href: '/modules/m09', label: 'Cronograma e alertas', icon: 'calendar', description: 'Itens manuais e derivados' },
      { href: '/modules/m10', label: 'Indicadores', icon: 'dashboard', description: 'Série derivada sem duplicar' },
      { href: '/modules/m11', label: 'Evidências', icon: 'folder-open', description: 'Documentos, versões e ciência' }
    ]
  },
  {
    id: 'programas-prevencao',
    label: 'Programas de prevenção',
    icon: 'shield',
    collapsible: true,
    items: [
      { href: '/modules/m12', label: 'Assédio e violência', icon: 'shield', description: 'Canal e tratamento de casos' },
      { href: '/modules/m13', label: 'Controle de EPI', icon: 'hard-hat', description: 'Entrega, validade e CA' },
      { href: '/modules/m14', label: 'Integração', icon: 'play', description: 'Onboarding e conformidade' },
      { href: '/modules/m15', label: 'Risco grave e iminente', icon: 'alert-triangle', description: 'Interdição e liberação' },
      { href: '/modules/m16', label: 'MEI, ME e EPP', icon: 'modules', description: 'Obrigações por porte' }
    ]
  },
  {
    id: 'conformidade-avancada',
    label: 'Conformidade avançada',
    icon: 'book',
    collapsible: true,
    items: [
      { href: '/modules/m17', label: 'Treinamentos EaD', icon: 'play', description: 'Checklist do Anexo II' },
      { href: '/modules/m18', label: 'Matriz legal', icon: 'book', description: 'SSOT legal e revisão' },
      { href: '/modules/m19', label: 'Análise crítica', icon: 'clipboard-check', description: 'Atas com vínculos operacionais' }
    ]
  },
  {
    id: 'expansao',
    label: 'Expansão',
    icon: 'folder-open',
    collapsible: true,
    default_collapsed: true,
    items: [
      { href: '/modules/m20', label: 'Inspeções', icon: 'eye', description: 'Inspeções, NCs e evidências' },
      { href: '/modules/m21', label: 'eSocial SST', icon: 'send', description: 'Eventos e recibos SST' },
      { href: '/modules/m22', label: 'Portal do colaborador', icon: 'user', description: 'Consumo com token e public_id' },
      { href: '/modules/m23', label: 'CIPA', icon: 'modules', description: 'Eleições e atas formais' },
      { href: '/modules/m24', label: 'ISO 45001', icon: 'shield', description: 'Correlação ISO × NR-01' },
      { href: '/modules/m25', label: 'Motor de IA', icon: 'auto', description: 'Assistência contextual governada' },
      { href: '/modules/m26', label: 'Avaliação 360°', icon: 'star', description: 'Feedback e PDI' },
      { href: '/modules/m27', label: 'Gestão de EPCs', icon: 'settings', description: 'Ativos e manutenção' },
      { href: '/modules/m28', label: 'Auditoria interna', icon: 'search', description: 'Plano e CAPAs integradas' }
    ]
  },
  {
    id: 'lgpd-seguranca',
    label: 'LGPD e segurança',
    icon: 'privacy',
    collapsible: true,
    items: [
      { href: '/privacy', label: 'LGPD operacional', icon: 'privacy', description: 'Consentimento e solicitações' },
      { href: '/security', label: 'Segurança operacional', icon: 'shield', description: 'Chaves, backup e auditoria' }
    ]
  }
]

/**
 * Menu SGQ (qualidade) — copy sem PGR/SST; módulos irmãos com rótulos comerciais.
 * Auditorias e NCs apontam para rotas dedicadas de workflow (F5/E2).
 */
const NAV_QUALIDADE: NavCategory[] = [
  {
    id: 'principal',
    label: 'Principal',
    icon: 'dashboard',
    collapsible: true,
    items: [
      { href: '/', label: 'Dashboard', icon: 'dashboard', description: 'Resumo da conformidade' },
      { href: '/conformidade', label: 'Painel de conformidade', icon: 'book', description: 'Biblioteca e % por norma', acl_tag: 'biblioteca:read' },
      { href: '/gap-analysis', label: 'Gap analysis', icon: 'search', description: 'Questionário e plano de ação', acl_tag: 'biblioteca:write' },
      { href: '/onboarding', label: 'Primeiros passos', icon: 'play', description: 'Ativação do trial', acl_tag: 'onboarding:read' },
      { href: '/admin/users', label: 'Usuários', icon: 'user', description: 'Contas e permissões', acl_tag: 'users:list' },
      { href: '/admin/acl', label: 'Permissões', icon: 'key', description: 'Grupos e ACL', acl_tag: 'acl:manage' },
      { href: '/admin/platform', label: 'Plataforma', icon: 'settings', description: 'Organizações e planos', platform_admin: true },
      { href: '/admin/integrations', label: 'Integrações', icon: 'settings', description: 'LLM, RAG e chaves de API', platform_admin: true }
    ]
  },
  {
    id: 'operacao-qualidade',
    label: 'Operação da qualidade',
    icon: 'clipboard-check',
    collapsible: true,
    items: [
      { href: '/modules/m11', label: 'Documentos', icon: 'folder-open', description: 'POPs, ITs, versionamento e distribuição' },
      { href: '/auditorias', label: 'Auditorias', icon: 'search', description: 'Planejamento, execução e achados', module_code: 'M28' },
      { href: '/nao-conformidades', label: 'Não conformidades', icon: 'alert-triangle', description: 'Registro, severidade e tratamento', module_code: 'M20' },
      { href: '/modules/m03', label: 'CAPA', icon: 'clipboard-check', description: 'Ações corretivas e preventivas' },
      { href: '/modules/m04', label: 'Treinamentos', icon: 'book', description: 'Matriz por função e execução' },
      { href: '/modules/m08', label: 'Fornecedores', icon: 'user', description: 'Qualificação e avaliação' }
    ]
  },
  {
    id: 'gestao-melhoria',
    label: 'Gestão e melhoria',
    icon: 'dashboard',
    collapsible: true,
    items: [
      { href: '/modules/m10', label: 'Indicadores', icon: 'dashboard', description: 'KPIs da qualidade com metas' },
      { href: '/modules/m09', label: 'Cronograma', icon: 'calendar', description: 'Prazos, alertas e recorrências' },
      { href: '/modules/m19', label: 'Análise crítica', icon: 'clipboard-check', description: 'Atas e decisões da direção' },
      { href: '/modules/m17', label: 'Treinamentos EaD', icon: 'play', description: 'Cursos e trilhas digitais' },
      { href: '/modules/m01', label: 'Configuração', icon: 'settings', description: 'Cadastros estruturais da organização' },
      { href: '/modules/m18', label: 'Matriz legal', icon: 'book', description: 'Requisitos legais e revisões' }
    ]
  },
  {
    id: 'lgpd-seguranca',
    label: 'LGPD e segurança',
    icon: 'privacy',
    collapsible: true,
    items: [
      { href: '/privacy', label: 'LGPD operacional', icon: 'privacy', description: 'Consentimento e solicitações' },
      { href: '/security', label: 'Segurança operacional', icon: 'shield', description: 'Chaves, backup e auditoria' }
    ]
  }
]

const NAV: NavCategory[] = PRODUCT.id === 'qualidade' ? NAV_QUALIDADE : NAV_SST

const DEFAULT_SHELL_MENU_PREFERENCES: ShellMenuPreferences = {
  favorite_keys: [],
  collapsed_categories: ['expansao']
}

let shell_menu_preferences: ShellMenuPreferences = { ...DEFAULT_SHELL_MENU_PREFERENCES }
let shell_menu_preferences_listener_registered = false
let shell_menu_search_listener_registered = false
let shell_appearance_listener_registered = false
let shell_global_search_listener_registered = false
let shell_document_listeners_registered = false
let shell_menu_auth_user: ShellAuthUser | null = null
let shell_menu_search_query = ''
let shell_appearance_persist_seq = 0

type MenuStatsDetails = {
  record_hint?: string
  alert_hint?: string
  error_hint?: string
}

type MenuStatsItem = {
  key: string
  href: string
  record_count: number
  alert_count: number
  error_count: number
  details?: MenuStatsDetails | null
}

type MenuStatsBundle = {
  organization_id: number | null
  interval_ms: number
  items: MenuStatsItem[]
  refreshed_at: string | null
}

let menu_stats_timer: number | null = null
let menu_stats_inflight = false
let menu_stats_last_bundle: MenuStatsBundle | null = null

const USER_SHORTCUTS: UserShortcutItem[] = [
  { href: '/security', label: 'Segurança', icon: 'shield' },
  { href: '/api/docs', label: 'API Docs', icon: 'book', external: true },
  { href: '/api/openapi.json', label: 'OpenAPI', icon: 'book', external: true },
  { href: '/privacy', label: 'LGPD', icon: 'privacy' }
]

type UiAppearancePreferences = {
  theme: ThemePreference
  ui_density: UiDensityPreference
}

function escape_html(value: string): string {
  return String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function path_matches(current: string, href: string): boolean {
  if (href === '/') return current === '/'
  return current === href || current.startsWith(`${href}/`)
}

function all_nav_items(): NavItem[] {
  return NAV.flatMap(category => category.items)
}

/** Metadados de nav para path do workspace (título/ícone da aba). */
export function nav_meta_for_path(path: string): { label: string; icon: IconName; acl_tag?: string } | null {
  const clean = String(path || '').split('?')[0].split('#')[0]
  if (!clean) return null
  if (clean === '/modules') return { label: 'Mapa de módulos', icon: 'modules' }
  for (const item of all_nav_items()) {
    if (item.href === clean) {
      return { label: item.label, icon: item.icon, acl_tag: item.acl_tag }
    }
  }
  return null
}

export function list_workspace_nav_paths(): string[] {
  const paths = new Set<string>(['/', '/modules'])
  for (const item of all_nav_items()) paths.add(item.href)
  paths.add('/account')
  return Array.from(paths)
}

function best_active_href(current: string): string | null {
  let best: string | null = null
  for (const item of all_nav_items()) {
    if (!path_matches(current, item.href)) continue
    if (best === null || item.href.length > best.length) best = item.href
  }
  return best
}

function is_active(current: string, href: string): boolean {
  if (!path_matches(current, href)) return false
  return best_active_href(current) === href
}

function category_has_active_item(category: NavCategory, current_path: string): boolean {
  return category.items.some(item => is_active(current_path, item.href))
}

function nav_item_key(item: NavItem): string {
  if (item.href === '/') return 'home'
  return item.href.replace(/^\/+/, '').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase()
}

function unique_valid_favorite_keys(values: unknown): string[] {
  if (!Array.isArray(values)) return []
  const valid = new Set(all_nav_items().map(item => nav_item_key(item)))
  const seen = new Set<string>()
  const result: string[] = []
  for (const raw of values) {
    const key = String(raw || '').trim().toLowerCase()
    if (!valid.has(key) || seen.has(key)) continue
    seen.add(key)
    result.push(key)
  }
  return result
}

function unique_valid_category_ids(values: unknown): string[] {
  if (!Array.isArray(values)) return []
  const valid = new Set(NAV.filter(category => category.collapsible).map(category => category.id))
  const seen = new Set<string>()
  const result: string[] = []
  for (const raw of values) {
    const id = String(raw || '').trim().toLowerCase()
    if (!valid.has(id) || seen.has(id)) continue
    seen.add(id)
    result.push(id)
  }
  return result
}

function normalize_shell_menu_preferences(
  value: unknown,
  fallback = DEFAULT_SHELL_MENU_PREFERENCES
): ShellMenuPreferences {
  const raw = value && typeof value === 'object'
    ? value as Partial<ShellMenuPreferences> & { favorite_hrefs?: unknown }
    : {}
  return {
    favorite_keys: unique_valid_favorite_keys(raw.favorite_keys ?? raw.favorite_hrefs ?? fallback.favorite_keys),
    collapsed_categories: unique_valid_category_ids(raw.collapsed_categories ?? fallback.collapsed_categories)
  }
}

function nav_item_allowed_for_user(item: NavItem, user: ShellAuthUser | null | undefined): boolean {
  // Item de plataforma: só para is_platform_root do /me (regra vive no backend;
  // wildcard/tag ACL de organização não conta).
  if (item.platform_admin && user?.is_platform_root !== true) return false
  // Entitlement da organização (F2/F5): módulo fora do plano some do menu
  // para TODOS — sem bypass de root/wildcard (a org não tem o módulo).
  const module_code =
    item.module_code ||
    (() => {
      const module_match = item.href.match(/^\/modules\/(m\d{2})$/)
      return module_match ? module_match[1].toUpperCase() : null
    })()
  if (module_code) {
    if (!user) return false
    if (Array.isArray(user.entitlements) && !user.entitlements.includes(module_code.toUpperCase())) {
      return false
    }
  }
  // Biblioteca de requisitos (F5): painel/gap some se a org não tem nenhuma library.
  if (item.href === '/conformidade' || item.href === '/gap-analysis') {
    if (!user) return false
    if (Array.isArray(user.library_entitlements) && user.library_entitlements.length === 0) {
      return false
    }
  }
  if (!item.acl_tag) return true
  if (!user) return false
  const role = String(user.role || '').trim().toLowerCase()
  const tags = Array.isArray(user.acl_tags) ? user.acl_tags : []
  if (role === 'root' || tags.includes('*')) return true
  return tags.includes(item.acl_tag)
}

function sidebar_nav_item_html(item: NavItem, current_path: string, opts: { pinned?: boolean } = {}): string {
  const active = is_active(current_path, item.href)
  const module_code =
    item.module_code ||
    (() => {
      const module_match = item.href.match(/^\/modules\/(m\d{2})$/)
      return module_match ? module_match[1].toUpperCase() : ''
    })()
  const module_attr = module_code ? ` data-module-code="${module_code}"` : ''
  // Item de plataforma nasce escondido (fail-closed) até o /me confirmar is_platform_root.
  const platform_hidden = Boolean(item.platform_admin && shell_menu_auth_user?.is_platform_root !== true)
  const platform_attr = item.platform_admin
    ? ` data-platform-admin="1"${platform_hidden ? ' data-nav-platform-hidden="1" style="display:none"' : ''}`
    : ''
  // Conformidade (F5): marca para o /me esconder se a org não tiver library.
  const library_attr =
    item.href === '/conformidade' || item.href === '/gap-analysis'
      ? ' data-requires-library="1"'
      : ''
  const acl_attr = (opts.pinned
    ? ''
    : item.acl_tag
      ? ` data-acl-tag="${escape_html(item.acl_tag)}"`
      : '') + module_attr + platform_attr + library_attr
  const key = nav_item_key(item)
  const safe_key = escape_html(key)
  const safe_href = escape_html(item.href)
  const favorite_active = shell_menu_preferences.favorite_keys.includes(key)
  const favorite_label = favorite_active
    ? `Remover ${item.label} dos favoritos`
    : `Adicionar ${item.label} aos favoritos`
  const search_text = escape_html(`${item.label} ${item.description || ''}`.toLowerCase())
  const title = escape_html(item.description || item.label)

  const base = 'group relative flex min-w-0 flex-1 items-center gap-2 rounded-sm border text-sm transition no-underline hover:no-underline'
  const state = active
    ? 'border-border bg-surface2 font-semibold text-ink'
    : 'border-transparent text-muted hover:border-border hover:bg-surface hover:text-ink'
  const icon_wrap = active
    ? 'bg-transparent text-ink'
    : 'bg-transparent text-muted group-hover:text-ink'

  return `
    <div
      data-nav-entry
      data-nav-entry-key="${safe_key}"
      data-nav-entry-href="${safe_href}"
      data-nav-search-text="${search_text}"
      ${opts.pinned ? 'data-nav-pinned="1"' : ''}
      ${acl_attr}
      class="flex min-w-0 items-stretch gap-1"
    >
      <a
        class="${base} ${state}"
        href="${safe_href}"
        data-nav-item
        data-nav-active="${active ? '1' : '0'}"
        data-nav-stats-href="${safe_href}"
        title="${title}"
      >
        <span class="grid shrink-0 place-items-center rounded-sm ${icon_wrap}" data-nav-icon>
          ${icon(item.icon, 'h-4 w-4')}
        </span>
        <span class="min-w-0 flex-1" data-nav-label>
          <span class="block font-medium leading-tight">${escape_html(item.label)}</span>
          ${item.description ? `<span class="block truncate text-xs text-muted">${escape_html(item.description)}</span>` : ''}
        </span>
        <span class="ml-auto hidden shrink-0 items-center gap-1 self-center" data-nav-stats-badges></span>
      </a>
      <button
        class="nav-favorite-button grid h-7 w-7 shrink-0 place-items-center self-center rounded-sm border border-transparent text-muted transition"
        type="button"
        data-nav-favorite-toggle="${safe_key}"
        data-nav-favorite-active="${favorite_active ? '1' : '0'}"
        aria-label="${escape_html(favorite_label)}"
        title="${escape_html(favorite_label)}"
      >
        ${icon('star', 'h-3.5 w-3.5')}
      </button>
    </div>
  `
}

function sidebar_nav_search_html(): string {
  return `
    <div class="px-3 pb-2 pt-2" data-nav-search-host>
      <label class="relative block">
        <span class="sr-only">Pesquisar no menu</span>
        <span class="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted">
          ${icon('search', 'h-3.5 w-3.5')}
        </span>
        <input
          class="h-8 w-full rounded-sm border border-border bg-surface2 pl-8 pr-2 text-xs text-ink placeholder:text-muted outline-none focus:border-brand"
          data-nav-search
          type="search"
          placeholder="Pesquisar no menu…"
          autocomplete="off"
          value="${escape_html(shell_menu_search_query)}"
        />
      </label>
    </div>
  `
}

function sidebar_nav_html(current_path: string): string {
  const favorites_section = `
    <section class="mt-1" data-nav-favorites-group style="display:none">
      <div class="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-muted" data-nav-group-label>
        <span class="grid h-7 w-7 shrink-0 place-items-center rounded-sm bg-warn/15 text-ink">
          ${icon('star', 'h-4 w-4')}
        </span>
        <span>Favoritos</span>
      </div>
      <div class="grid" data-nav-items data-nav-favorites-host></div>
    </section>
  `

  const collapsed_set = new Set(shell_menu_preferences.collapsed_categories)
  const category_sections = NAV.map(category => {
    const has_active = category_has_active_item(category, current_path)
    const use_collapsed = Boolean(category.collapsible && !has_active && collapsed_set.has(category.id))

    const label_html = category.collapsible
      ? `
        <button
          class="flex w-full items-center gap-2 text-left text-[10px] font-bold uppercase tracking-wide text-muted hover:text-ink"
          type="button"
          data-nav-group-label
          data-nav-group-toggle
          aria-expanded="${use_collapsed ? 'false' : 'true'}"
        >
          <span class="grid h-8 w-8 shrink-0 place-items-center rounded-sm bg-surface2 text-ink" data-nav-group-icon>
            ${icon(category.icon, 'h-5 w-5')}
          </span>
          <span class="min-w-0 flex-1 truncate text-[11px]">${escape_html(category.label)}</span>
          <span class="grid h-5 w-5 shrink-0 place-items-center rounded-sm" data-nav-group-chevron>
            ${icon('chevron-down', 'h-3.5 w-3.5')}
          </span>
        </button>
      `
      : `
        <div class="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide text-muted" data-nav-group-label>
          <span class="grid h-8 w-8 shrink-0 place-items-center rounded-sm bg-surface2 text-ink" data-nav-group-icon>
            ${icon(category.icon, 'h-5 w-5')}
          </span>
          <span class="text-[11px]">${escape_html(category.label)}</span>
        </div>
      `

    return `
      <section
        class="mt-1"
        data-nav-group
        data-nav-group-key="${escape_html(category.id)}"
        ${category.collapsible ? 'data-nav-collapsible="1"' : ''}
        data-nav-group-collapsed="${use_collapsed ? '1' : '0'}"
      >
        ${label_html}
        <div class="grid ${use_collapsed ? 'hidden' : ''}" data-nav-items>
          ${category.items.map(item => sidebar_nav_item_html(item, current_path)).join('\n')}
        </div>
      </section>
    `
  }).join('\n')

  return `${sidebar_nav_search_html()}\n${favorites_section}\n${category_sections}`
}

function set_nav_group_collapsed(category_id: string, collapsed: boolean): void {
  document.querySelectorAll<HTMLElement>(`[data-nav-group-key="${category_id}"]`).forEach(group => {
    const items = group.querySelector<HTMLElement>('[data-nav-items]')
    const toggle = group.querySelector<HTMLElement>('[data-nav-group-toggle]')
    group.setAttribute('data-nav-group-collapsed', collapsed ? '1' : '0')
    items?.classList.toggle('hidden', collapsed)
    toggle?.setAttribute('aria-expanded', collapsed ? 'false' : 'true')
  })
}

function paint_nav_favorite_buttons(preferences: ShellMenuPreferences): void {
  const favorites = new Set(preferences.favorite_keys)
  document.querySelectorAll<HTMLButtonElement>('[data-nav-favorite-toggle]').forEach(button => {
    const key = String(button.getAttribute('data-nav-favorite-toggle') || '').trim()
    const active = favorites.has(key)
    const entry = button.closest<HTMLElement>('[data-nav-entry]')
    const label = String(entry?.querySelector<HTMLElement>('[data-nav-label]')?.textContent || 'item').trim()
    const title = active ? `Remover ${label} dos favoritos` : `Adicionar ${label} aos favoritos`
    button.setAttribute('data-nav-favorite-active', active ? '1' : '0')
    button.setAttribute('aria-label', title)
    button.setAttribute('title', title)
  })
}

function favorite_nav_items_for_user(
  user: ShellAuthUser | null | undefined,
  preferences: ShellMenuPreferences
): NavItem[] {
  const by_key = new Map(all_nav_items().map(item => [nav_item_key(item), item] as const))
  const seen = new Set<string>()
  const result: NavItem[] = []
  for (const key of preferences.favorite_keys) {
    const item = by_key.get(key)
    if (!item || !nav_item_allowed_for_user(item, user)) continue
    if (seen.has(item.href)) continue
    seen.add(item.href)
    result.push(item)
  }
  return result
}

function paint_nav_favorites(
  user: ShellAuthUser | null | undefined,
  preferences: ShellMenuPreferences
): void {
  const items = favorite_nav_items_for_user(user, preferences)
  const current_path = window.location.pathname
  const html = items.map(item => sidebar_nav_item_html(item, current_path, { pinned: true })).join('')

  document.querySelectorAll<HTMLElement>('[data-nav-favorites-host]').forEach(host => {
    host.innerHTML = html
  })
  document.querySelectorAll<HTMLElement>('[data-nav-favorites-group]').forEach(group => {
    group.style.display = items.length ? '' : 'none'
  })

  paint_nav_favorite_buttons(preferences)
  apply_nav_search_filter(shell_menu_search_query)
}

function apply_shell_menu_preferences(
  user: ShellAuthUser | null | undefined,
  preferences: ShellMenuPreferences
): void {
  shell_menu_preferences = normalize_shell_menu_preferences(preferences, {
    favorite_keys: [],
    collapsed_categories: []
  })

  const current_path = window.location.pathname
  const collapsed = new Set(shell_menu_preferences.collapsed_categories)
  NAV.forEach(category => {
    if (!category.collapsible) return
    const should_collapse = collapsed.has(category.id) && !category_has_active_item(category, current_path)
    set_nav_group_collapsed(category.id, should_collapse)
  })

  paint_nav_favorites(user, shell_menu_preferences)
  paint_nav_favorite_buttons(shell_menu_preferences)
  if (menu_stats_last_bundle) paint_menu_stats(menu_stats_last_bundle)
}

async function load_shell_menu_preferences(): Promise<ShellMenuPreferences> {
  try {
    const preferences = await api_json<ShellMenuPreferences>('/api/auth/shell-menu-preferences')
    return normalize_shell_menu_preferences(preferences, DEFAULT_SHELL_MENU_PREFERENCES)
  } catch {
    return normalize_shell_menu_preferences(DEFAULT_SHELL_MENU_PREFERENCES)
  }
}

async function persist_shell_menu_preferences(preferences: ShellMenuPreferences): Promise<ShellMenuPreferences> {
  const payload = normalize_shell_menu_preferences(preferences, { favorite_keys: [], collapsed_categories: [] })
  const saved = await api_json<ShellMenuPreferences>('/api/auth/shell-menu-preferences', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
  return normalize_shell_menu_preferences(saved, payload)
}

async function toggle_nav_favorite(key: string): Promise<void> {
  if (!shell_menu_auth_user) return
  const item = all_nav_items().find(candidate => nav_item_key(candidate) === key)
  if (!item || !nav_item_allowed_for_user(item, shell_menu_auth_user)) return

  const previous = normalize_shell_menu_preferences(shell_menu_preferences, {
    favorite_keys: [],
    collapsed_categories: []
  })
  const next_favorites = previous.favorite_keys.includes(key)
    ? previous.favorite_keys.filter(item_key => item_key !== key)
    : [...previous.favorite_keys, key]
  const next = { ...previous, favorite_keys: next_favorites }

  apply_shell_menu_preferences(shell_menu_auth_user, next)
  try {
    const saved = await persist_shell_menu_preferences(next)
    apply_shell_menu_preferences(shell_menu_auth_user, saved)
  } catch {
    apply_shell_menu_preferences(shell_menu_auth_user, previous)
  }
}

async function toggle_nav_group(category_id: string): Promise<void> {
  const category = NAV.find(item => item.id === category_id && item.collapsible)
  if (!category) return

  const current_group = document.querySelector<HTMLElement>(`[data-nav-group-key="${category_id}"]`)
  const currently_collapsed = current_group?.getAttribute('data-nav-group-collapsed') === '1'
  const previous = normalize_shell_menu_preferences(shell_menu_preferences, {
    favorite_keys: [],
    collapsed_categories: []
  })
  const collapsed = new Set(previous.collapsed_categories)
  if (currently_collapsed) collapsed.delete(category_id)
  else collapsed.add(category_id)
  const next = { ...previous, collapsed_categories: Array.from(collapsed) }

  // Aplica na hora; persiste se autenticado. Sem sessão, o estado vale só na página atual.
  apply_shell_menu_preferences(shell_menu_auth_user, next)
  if (!shell_menu_auth_user) return
  try {
    const saved = await persist_shell_menu_preferences(next)
    apply_shell_menu_preferences(shell_menu_auth_user, saved)
  } catch {
    apply_shell_menu_preferences(shell_menu_auth_user, previous)
  }
}

function normalize_nav_search(value: string): string {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
}

function entry_is_acl_hidden(entry: HTMLElement): boolean {
  return entry.getAttribute('data-nav-acl-hidden') === '1'
}

function entry_is_entitlement_hidden(entry: HTMLElement): boolean {
  return entry.getAttribute('data-nav-entitlement-hidden') === '1'
}

function entry_is_platform_hidden(entry: HTMLElement): boolean {
  return entry.getAttribute('data-nav-platform-hidden') === '1'
}

function entry_is_stats_hidden(entry: HTMLElement): boolean {
  return entry.getAttribute('data-nav-entry-hidden-by-stats') === '1'
}

function apply_nav_search_filter(query: string): void {
  shell_menu_search_query = query
  const normalized = normalize_nav_search(query)
  const searching = normalized.length > 0

  document.querySelectorAll<HTMLInputElement>('[data-nav-search]').forEach(input => {
    if (input.value !== query) input.value = query
  })

  document.querySelectorAll<HTMLElement>('[data-nav-entry]').forEach(entry => {
    if (entry_is_acl_hidden(entry) || entry_is_entitlement_hidden(entry) || entry_is_platform_hidden(entry) || entry_is_stats_hidden(entry)) {
      entry.style.display = 'none'
      return
    }
    if (!searching) {
      entry.style.display = ''
      return
    }
    const text = normalize_nav_search(String(entry.getAttribute('data-nav-search-text') || ''))
    entry.style.display = text.includes(normalized) ? '' : 'none'
  })

  document.querySelectorAll<HTMLElement>('[data-nav-group]').forEach(group => {
    const entries = Array.from(group.querySelectorAll<HTMLElement>('[data-nav-entry]'))
    const has_visible = entries.some(entry => (
      entry.style.display !== 'none' &&
      !entry_is_acl_hidden(entry) &&
      !entry_is_entitlement_hidden(entry) &&
      !entry_is_platform_hidden(entry) &&
      !entry_is_stats_hidden(entry)
    ))
    group.style.display = has_visible ? '' : 'none'

    if (searching && has_visible) {
      const items = group.querySelector<HTMLElement>('[data-nav-items]')
      const toggle = group.querySelector<HTMLElement>('[data-nav-group-toggle]')
      group.setAttribute('data-nav-group-collapsed', '0')
      items?.classList.remove('hidden')
      toggle?.setAttribute('aria-expanded', 'true')
    }
  })

  document.querySelectorAll<HTMLElement>('[data-nav-favorites-group]').forEach(group => {
    const entries = Array.from(group.querySelectorAll<HTMLElement>('[data-nav-entry]'))
    const has_visible = entries.some(entry => (
      entry.style.display !== 'none' &&
      !entry_is_acl_hidden(entry) &&
      !entry_is_entitlement_hidden(entry) &&
      !entry_is_platform_hidden(entry) &&
      !entry_is_stats_hidden(entry)
    ))
    const has_any = entries.length > 0
    group.style.display = has_any && has_visible ? '' : 'none'
  })

  if (!searching) {
    const collapsed = new Set(shell_menu_preferences.collapsed_categories)
    const current_path = window.location.pathname
    NAV.forEach(category => {
      if (!category.collapsible) return
      const should_collapse = collapsed.has(category.id) && !category_has_active_item(category, current_path)
      set_nav_group_collapsed(category.id, should_collapse)
    })
  }
}

function sync_nav_group_visibility(): void {
  document.querySelectorAll<HTMLElement>('[data-nav-group]').forEach(group => {
    const entries = Array.from(group.querySelectorAll<HTMLElement>('[data-nav-entry]'))
    const has_visible = entries.some(entry => (
      entry.style.display !== 'none' &&
      !entry_is_acl_hidden(entry) &&
      !entry_is_entitlement_hidden(entry) &&
      !entry_is_platform_hidden(entry) &&
      !entry_is_stats_hidden(entry)
    ))
    // When searching, apply_nav_search_filter already set visibility.
    if (!normalize_nav_search(shell_menu_search_query)) {
      group.style.display = has_visible ? '' : 'none'
    }
  })
}

function ensure_shell_menu_preferences_listener(): void {
  if (shell_menu_preferences_listener_registered) return

  document.addEventListener('click', event => {
    const favorite_button = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-nav-favorite-toggle]')
    if (favorite_button) {
      event.preventDefault()
      event.stopPropagation()
      const key = String(favorite_button.getAttribute('data-nav-favorite-toggle') || '').trim()
      if (key) void toggle_nav_favorite(key)
      return
    }

    const group_toggle = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-nav-group-toggle]')
    if (group_toggle) {
      event.preventDefault()
      const group = group_toggle.closest<HTMLElement>('[data-nav-group-key]')
      const category_id = String(group?.getAttribute('data-nav-group-key') || '').trim()
      if (category_id) void toggle_nav_group(category_id)
    }
  })

  shell_menu_preferences_listener_registered = true
}

function ensure_shell_menu_search_listener(): void {
  if (shell_menu_search_listener_registered) return

  document.addEventListener('input', event => {
    const input = (event.target as HTMLElement | null)?.closest<HTMLInputElement>('[data-nav-search]')
    if (!input) return
    apply_nav_search_filter(input.value)
  })

  document.addEventListener('keydown', event => {
    const input = (event.target as HTMLElement | null)?.closest<HTMLInputElement>('[data-nav-search]')
    if (!input) return
    if (event.key === 'Enter') event.preventDefault()
  })

  shell_menu_search_listener_registered = true
}

function user_can_see_module_map(user: ShellAuthUser | null | undefined): boolean {
  if (!user) return false
  const role = String(user.role || '').trim().toLowerCase()
  const tags = Array.isArray(user.acl_tags) ? user.acl_tags : []
  if (role === 'root' || tags.includes('*')) return true
  return tags.includes('acl:manage') || tags.includes('users:list')
}

function user_shortcuts_html(): string {
  return USER_SHORTCUTS.map(item => {
    const external = item.external ? ' target="_blank" rel="noreferrer"' : ''
    return `
      <a class="flex items-center gap-2 border-b border-border px-3 py-2.5 text-sm text-ink no-underline hover:no-underline last:border-b-0 hover:bg-surface2" href="${item.href}"${external}>
        ${icon(item.icon)} ${item.label}
      </a>
    `
  }).join('\n')
}

function appearance_cycle_button_html(
  kind: 'theme' | 'density',
  preference: ThemePreference | UiDensityPreference,
  variant: 'topbar' | 'menu' | 'drawer' = 'topbar'
): string {
  if (kind === 'theme') {
    const pref = preference as ThemePreference
    const label = theme_preference_label(pref)
    const cls = variant === 'topbar'
      ? 'inline-flex h-9 w-9 items-center justify-center rounded-sm border border-white/25 bg-white/10 text-white hover:bg-white/15'
      : variant === 'drawer'
        ? 'inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-sm border border-border bg-surface2 px-3 text-sm text-ink hover:bg-surface'
        : 'flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-ink hover:bg-surface2'
    const content = variant === 'topbar'
      ? icon(theme_preference_icon(pref), 'h-4 w-4')
      : `${icon(theme_preference_icon(pref))} Tema: <span class="font-medium" data-appearance-label="theme">${escape_html(label)}</span>`
    return `
      <button
        class="${cls}"
        type="button"
        data-appearance-cycle="theme"
        data-appearance-variant="${variant}"
        title="Tema: ${escape_html(label)} (clique para alternar)"
        aria-label="Alternar tema. Atual: ${escape_html(label)}"
      >${content}</button>
    `
  }

  const pref = preference as UiDensityPreference
  const label = ui_density_preference_label(pref)
  const cls = variant === 'topbar'
    ? 'inline-flex h-9 w-9 items-center justify-center rounded-sm border border-white/25 bg-white/10 text-white hover:bg-white/15'
    : variant === 'drawer'
      ? 'inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-sm border border-border bg-surface2 px-3 text-sm text-ink hover:bg-surface'
      : 'flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-ink hover:bg-surface2'
  const content = variant === 'topbar'
    ? icon(ui_density_preference_icon(pref), 'h-4 w-4')
    : `${icon(ui_density_preference_icon(pref))} Layout: <span class="font-medium" data-appearance-label="density">${escape_html(label)}</span>`
  return `
    <button
      class="${cls}"
      type="button"
      data-appearance-cycle="density"
      data-appearance-variant="${variant}"
      title="Layout: ${escape_html(label)} (clique para alternar)"
      aria-label="Alternar layout. Atual: ${escape_html(label)}"
    >${content}</button>
  `
}

function user_slot_html(opts: {
  label: string
  email: string
  meta: string
  avatar_url?: string | null
  authenticated: boolean
  topbar?: boolean
  show_module_map?: boolean
  app_version?: string | null
  organizations?: Array<{ id: number; name: string; active?: boolean }>
}): string {
  const label = escape_html(opts.label)
  const email = escape_html(opts.email)
  const meta = escape_html(opts.meta)
  const btn_cls = opts.topbar
    ? 'inline-flex h-9 w-9 items-center justify-center rounded-sm text-white hover:ring-2 hover:ring-white/30'
    : 'inline-flex h-9 w-9 items-center justify-center rounded-sm text-ink hover:bg-surface2'
  const density = get_ui_density_preference()
  const theme = get_theme_preference()
  const version = String(opts.app_version || '').trim()

  if (!opts.authenticated) {
    return `
      <a class="${btn_cls}" href="/login" title="Entrar" aria-label="Entrar">
        ${icon('user', 'h-4 w-4')}
      </a>
    `
  }

  const orgs = opts.organizations || []
  const org_block =
    orgs.length > 1
      ? `<div class="border-b border-border px-3 py-2">
          <div class="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted">Organizações (workspace)</div>
          ${orgs
            .map(
              (o) => `
            <button type="button" class="flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm ${
              o.active ? 'bg-surface2 font-semibold text-ink' : 'text-ink hover:bg-surface2'
            }" data-switch-org="${o.id}" ${o.active ? 'aria-current="true"' : ''}>
              <span class="truncate">${escape_html(o.name)}</span>
              ${o.active ? '<span class="text-[10px] uppercase text-muted">atual</span>' : ''}
            </button>`
            )
            .join('')}
        </div>`
      : ''

  return `
    <div class="relative" data-profile-host>
      <button class="${btn_cls}" id="nr01-profile-btn" type="button" aria-haspopup="menu" aria-expanded="false" title="${label}" aria-label="Menu da conta e perfil">
        ${avatar_html({
          url: opts.avatar_url,
          name: opts.label,
          email: opts.email,
          size: 'md',
          topbar: Boolean(opts.topbar),
          className: opts.topbar ? 'ring-white/30' : ''
        })}
      </button>
      <div class="absolute right-0 z-[80] mt-2 hidden min-w-[280px] overflow-hidden rounded-sm border border-border bg-surface shadow-lg" id="nr01-profile-menu" role="menu">
        <div class="flex items-center gap-3 border-b border-border px-3 py-3">
          ${avatar_html({ url: opts.avatar_url, name: opts.label, email: opts.email, size: 'lg' })}
          <div class="min-w-0">
            <div class="truncate text-sm font-semibold text-ink">${label}</div>
            <div class="truncate text-xs text-muted">${email}</div>
            <div class="mt-0.5 text-[11px] uppercase tracking-wide text-muted">${meta}</div>
            ${version ? `<div class="mt-1 text-[11px] text-muted" data-profile-version>Versão ${escape_html(version)}</div>` : ''}
          </div>
        </div>
        ${org_block}
        <a class="flex items-center gap-2 px-3 py-2.5 text-sm text-ink no-underline hover:no-underline hover:bg-surface2" href="/account">${icon('user')} Minha conta</a>
        ${opts.show_module_map
          ? `<a class="flex items-center gap-2 px-3 py-2.5 text-sm text-ink no-underline hover:no-underline hover:bg-surface2" href="/modules">${icon('modules')} Mapa de módulos</a>`
          : ''}
        ${appearance_cycle_button_html('theme', theme, 'menu')}
        ${appearance_cycle_button_html('density', density, 'menu')}
        ${user_shortcuts_html()}
        <button class="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-ink hover:bg-surface2" id="nr01-logout" type="button">${icon('logout')} Sair</button>
      </div>
    </div>
  `
}

function global_search_html(): string {
  return `
    <div class="relative hidden lg:block" data-global-search-host>
      <label class="relative block">
        <span class="sr-only">Buscar</span>
        <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/70">${icon('search', 'h-4 w-4')}</span>
        <input
          class="h-9 w-[220px] rounded-sm border border-white/25 bg-white/10 pl-9 pr-3 text-sm text-white placeholder:text-white/60 outline-none focus:border-white/50 xl:w-[280px]"
          data-global-search
          type="search"
          placeholder="Buscar módulo, página…"
          autocomplete="off"
          aria-autocomplete="list"
          aria-controls="nr01-global-search-results"
          aria-expanded="false"
        />
      </label>
      <div
        class="absolute right-0 z-50 mt-2 hidden max-h-80 w-[min(100vw-2rem,360px)] overflow-y-auto rounded-sm border border-border bg-surface"
        id="nr01-global-search-results"
        data-global-search-results
        role="listbox"
        hidden
      ></div>
    </div>
  `
}

/** Dois botões independentes no header: tema e layout (estado fixado no perfil). */
function appearance_controls_html(theme: ThemePreference, density: UiDensityPreference): string {
  return `
    <div class="inline-flex items-center gap-1" role="group" aria-label="Aparência da interface">
      ${appearance_cycle_button_html('theme', theme, 'topbar')}
      ${appearance_cycle_button_html('density', density, 'topbar')}
    </div>
  `
}

function mfa_banner_html(): string {
  return `
    <div class="hidden border-b border-warn/30 bg-warn/10 px-3 py-2 sm:px-5" id="nr01-mfa-banner">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <p class="text-sm text-ink" id="nr01-mfa-banner-text"></p>
        <a class="inline-flex items-center gap-1.5 rounded-sm bg-action px-3 py-1.5 text-xs font-semibold text-action-ink hover:bg-action/90" href="/account?tab=seguranca" id="nr01-mfa-banner-cta">
          ${icon('shield', 'h-3.5 w-3.5')} Ativar 2FA
        </a>
      </div>
    </div>
  `
}

function brand_mark_html(variant: 'topbar' | 'surface' = 'topbar'): string {
  const cls = variant === 'topbar'
    ? 'grid h-9 w-9 place-items-center rounded-sm bg-white/15 text-white ring-1 ring-white/20'
    : 'grid h-9 w-9 place-items-center rounded-sm bg-brand text-brand-ink ring-1 ring-border'
  return `
    <span class="${cls}">
      ${icon('hard-hat', 'h-5 w-5')}
    </span>
  `
}

/** Badge de ambiente (Trial/Ativo) — versão vai no menu do perfil. */
function env_badge_html(): string {
  return `
    <span class="hidden items-center gap-1.5 rounded-sm border border-white/25 bg-white/10 px-2.5 py-1 text-xs font-medium text-white sm:inline-flex" title="Ambiente">
      ${icon('settings', 'h-3.5 w-3.5')}
      <span id="nr01-env-mode">…</span>
    </span>
  `
}

/** Badges de sistemas ativos (troca rápida). */
function systems_quick_html(): string {
  return `
    <div class="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto" id="nr01-systems-quick" data-systems-quick
      role="navigation" aria-label="Sistemas ativos — clique para trocar">
      <span class="shrink-0 rounded-sm border border-white/30 bg-white/15 px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-white"
        title="Área de troca entre sistemas">${escape_html(PRODUCT.sigla || PRODUCT.brand)}</span>
      <span class="hidden text-[11px] text-white/70 sm:inline" id="nr01-systems-quick-hint">Trocar sistema</span>
    </div>
  `
}

function systems_switcher_panel_html(): string {
  return `
    <div id="nr01-systems-panel" class="fixed inset-0 z-[90] hidden" data-systems-panel aria-hidden="true">
      <button type="button" class="absolute inset-0 bg-black/40" data-systems-panel-close aria-label="Fechar seletor de sistemas"></button>
      <div class="absolute left-3 top-16 w-[min(100vw-1.5rem,380px)] overflow-hidden rounded-sm border border-border bg-surface shadow-xl sm:left-4 lg:left-[calc(280px+1rem)]"
        role="dialog" aria-labelledby="nr01-systems-panel-title">
        <div class="border-b border-border px-4 py-3">
          <div class="flex items-start justify-between gap-2">
            <div>
              <p class="text-[10px] font-bold uppercase tracking-wide text-muted">Troca de sistema</p>
              <h2 class="text-sm font-semibold text-ink" id="nr01-systems-panel-title">NR-01 e SGQ</h2>
            </div>
            <button type="button" class="inline-flex h-8 w-8 items-center justify-center rounded-sm text-muted hover:bg-surface2" data-systems-panel-close aria-label="Fechar">
              ${icon('close', 'h-4 w-4')}
            </button>
          </div>
          <div class="mt-3 flex gap-1 rounded-sm border border-border bg-surface2 p-0.5" role="tablist">
            <button type="button" role="tab" aria-selected="true" data-systems-tab="active"
              class="flex-1 rounded-sm px-2 py-1.5 text-xs font-semibold text-ink bg-surface shadow-sm">Sistemas ativos</button>
            <button type="button" role="tab" aria-selected="false" data-systems-tab="available"
              class="flex-1 rounded-sm px-2 py-1.5 text-xs font-semibold text-muted hover:text-ink">Sistemas disponíveis</button>
          </div>
        </div>
        <div class="max-h-[min(70vh,420px)] overflow-y-auto p-3" data-systems-panel-body>
          <p class="text-sm text-muted">Carregando…</p>
        </div>
      </div>
    </div>
  `
}

function environment_badge_label(raw: string): string {
  const mode = raw.trim().toLowerCase()
  if (mode === 'demo' || mode === 'trial' || mode === 'homolog' || mode === 'homologacao' || mode === 'homologação') return 'Trial'
  if (mode === 'producao' || mode === 'produção' || mode === 'production' || mode === 'prod') return 'Ativo'
  return raw.trim() || 'n/a'
}

/** Cabeçalho + conteúdo da página (painel de aba do workspace — sem shell). */
export function page_content_html(opts: {
  title: string
  subtitle?: string | null
  header_actions_html?: string | null
  content_html: string
}): string {
  return `
    <div class="nr-page-content" data-page-content>
      <div class="flex flex-wrap items-start justify-between gap-3" id="nr01-page-header">
        <div class="min-w-0">
          <h1 class="text-xl font-semibold tracking-tight text-ink sm:text-2xl" id="nr01-page-title">${escape_html(opts.title)}</h1>
          ${opts.subtitle
            ? `<p class="mt-1 max-w-3xl text-sm text-muted" id="nr01-page-subtitle">${opts.subtitle}</p>`
            : '<p class="hidden mt-1 max-w-3xl text-sm text-muted" id="nr01-page-subtitle"></p>'}
        </div>
        ${opts.header_actions_html
          ? `<div class="flex flex-wrap items-center gap-3">${opts.header_actions_html}</div>`
          : ''}
      </div>
      ${opts.content_html}
    </div>
  `
}

/** Shell persistente do workspace: sidebar + topbar + faixa de abas + outlet. */
export function workspace_shell_html(active_path: string): string {
  const pref = get_theme_preference()
  const ui_density = get_ui_density_preference()

  return `
    <a class="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-sm focus:bg-surface focus:px-3 focus:py-2 focus:text-sm" href="#main">
      Pular para o conteúdo
    </a>

    <div id="nr01-shell" data-workspace="1" data-sidebar-available="1">
      <aside
        id="nr01-sidebar-desktop"
        class="fixed inset-y-0 left-0 z-30 hidden w-[280px] flex-col overflow-hidden border-r border-border bg-surface lg:flex"
      >
        <div class="flex h-14 items-center gap-3 border-b border-border px-4">
          <button type="button" class="flex min-w-0 flex-1 items-center gap-3 rounded-sm text-left hover:bg-surface2 -mx-1 px-1 py-1"
            id="nr01-systems-trigger" data-systems-open
            title="Trocar de sistema / organização" aria-haspopup="dialog" aria-controls="nr01-systems-panel">
            ${brand_mark_html('surface')}
            <span class="min-w-0 flex-1">
              <span class="flex items-center gap-1.5">
                <span class="block truncate text-sm font-semibold text-ink" data-brand-name>${PRODUCT.brand}</span>
                <span class="shrink-0 rounded-sm border border-border bg-surface2 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-muted"
                  title="Clique para trocar de sistema">Sistemas</span>
              </span>
              <span class="block truncate text-xs text-muted" data-brand-org>${PRODUCT.tagline}</span>
            </span>
            ${icon('chevron-down', 'h-4 w-4 shrink-0 text-muted')}
          </button>
        </div>
        <nav class="flex-1 overflow-y-auto pb-4 pt-2">
          ${sidebar_nav_html(active_path)}
        </nav>
      </aside>

      <div class="flex min-h-screen flex-col lg:pl-[280px]">
        <header class="nr01-topbar sticky top-0 z-40 flex h-14 items-center gap-2 px-3 sm:px-5">
          <button class="inline-flex h-10 w-10 items-center justify-center rounded-sm text-white hover:bg-white/10 lg:hidden" id="nr01-nav-open" type="button" aria-label="Abrir menu">
            ${icon('menu', 'h-5 w-5')}
          </button>

          <div class="flex min-w-0 flex-1 items-center gap-2">
            ${systems_quick_html()}
            ${env_badge_html()}
          </div>

          <div class="flex shrink-0 items-center gap-1.5">
            ${global_search_html()}
            ${appearance_controls_html(pref, ui_density)}
            <div id="nr01-user-slot">
              ${user_slot_html({
                label: 'Conta',
                email: '',
                meta: 'Não autenticado',
                authenticated: false,
                topbar: true
              })}
            </div>
          </div>
        </header>
        ${mfa_banner_html()}

        <div
          id="nr01-tab-bar"
          class="nr01-tab-bar flex h-9 shrink-0 items-stretch overflow-x-auto border-b border-border bg-surface2"
          role="tablist"
          aria-label="Aplicativos abertos"
        ></div>

        <div class="nr01-shell-content flex-1">
          <main id="main">
            <div id="nr01-workspace-outlet" class="nr01-workspace-outlet" data-workspace-outlet></div>
          </main>
        </div>
      </div>

      ${systems_switcher_panel_html()}

      <div class="fixed inset-0 z-50 hidden lg:hidden" id="nr01-drawer">
        <button class="absolute inset-0 bg-black/40" id="nr01-nav-close" aria-label="Fechar menu"></button>
        <aside class="absolute left-0 top-0 flex h-full w-[280px] max-w-[92vw] flex-col border-r border-border bg-surface">
          <div class="flex h-14 items-center justify-between gap-2 border-b border-border px-3">
            <button type="button" class="inline-flex min-w-0 items-center gap-2 text-left" data-systems-open title="Trocar de sistema">
              ${brand_mark_html('surface')}
              <span class="truncate text-sm font-semibold text-ink">${PRODUCT.brand}</span>
              <span class="rounded-sm border border-border bg-surface2 px-1 text-[9px] font-bold uppercase text-muted">Sistemas</span>
            </button>
            <button class="inline-flex h-9 w-9 items-center justify-center rounded-sm text-muted hover:bg-surface2 hover:text-ink" id="nr01-nav-close-btn" type="button" aria-label="Fechar menu">
              ${icon('close', 'h-5 w-5')}
            </button>
          </div>
          <div class="border-b border-border px-3 py-3">
            <div class="mb-2 text-[10px] font-bold uppercase tracking-wide text-muted">Aparência</div>
            <div class="flex gap-2">
              ${appearance_cycle_button_html('theme', pref, 'drawer')}
              ${appearance_cycle_button_html('density', ui_density, 'drawer')}
            </div>
          </div>
          <nav class="flex-1 overflow-y-auto pb-3 pt-2">
            ${sidebar_nav_html(active_path)}
          </nav>
        </aside>
      </div>
    </div>
  `
}

/** Atualiza estado ativo do menu sem remount do shell. */
export function sync_shell_active_path(current_path: string): void {
  const clean = String(current_path || '').split('?')[0].split('#')[0]
  const active_href = best_active_href(clean)

  document.querySelectorAll<HTMLAnchorElement>('[data-nav-item]').forEach(anchor => {
    const href = anchor.getAttribute('href') || ''
    const active = Boolean(active_href && href === active_href)
    anchor.setAttribute('data-nav-active', active ? '1' : '0')
    const base = 'group relative flex min-w-0 flex-1 items-center gap-2 rounded-sm border text-sm transition-colors no-underline hover:no-underline'
    const state = active
      ? 'border-border bg-surface2 font-semibold text-ink'
      : 'border-transparent text-muted hover:border-border hover:bg-surface hover:text-ink'
    anchor.className = `${base} ${state}`
    const icon_wrap = anchor.querySelector<HTMLElement>('[data-nav-icon]')
    if (icon_wrap) {
      icon_wrap.className = active
        ? 'grid shrink-0 place-items-center rounded-sm bg-transparent text-ink'
        : 'grid shrink-0 place-items-center rounded-sm bg-transparent text-muted group-hover:text-ink'
    }
  })

  // Título da página fica na aba; topbar mostra sistemas ativos (não o nome da rota).
  void clean
}

export function shell_html(opts: {
  title: string
  subtitle?: string | null
  header_actions_html?: string | null
  active_path: string
  content_html: string
  wide?: boolean
  hide_sidebar?: boolean
}): string {
  const pref = get_theme_preference()
  const ui_density = get_ui_density_preference()
  const sidebar_available = !opts.hide_sidebar

  return `
    <a class="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-sm focus:bg-surface focus:px-3 focus:py-2 focus:text-sm" href="#main">
      Pular para o conteúdo
    </a>

    <div id="nr01-shell" data-sidebar-available="${sidebar_available ? '1' : '0'}">
      ${sidebar_available ? `
      <aside
        id="nr01-sidebar-desktop"
        class="fixed inset-y-0 left-0 z-30 hidden w-[280px] flex-col overflow-hidden border-r border-border bg-surface lg:flex"
      >
        <div class="flex h-14 items-center gap-3 border-b border-border px-4">
          <a class="flex min-w-0 items-center gap-3 no-underline hover:no-underline" href="/">
            ${brand_mark_html('surface')}
            <span class="min-w-0">
              <span class="block truncate text-sm font-semibold text-ink">${PRODUCT.brand}</span>
              <span class="block truncate text-xs text-muted" data-brand-org>${PRODUCT.tagline}</span>
            </span>
          </a>
        </div>
        <nav class="flex-1 overflow-y-auto pb-4 pt-2">
          ${sidebar_nav_html(opts.active_path)}
        </nav>
      </aside>
      ` : ''}

      <div class="flex min-h-screen flex-col ${sidebar_available ? 'lg:pl-[280px]' : ''}">
        <header class="nr01-topbar sticky top-0 z-40 flex h-14 items-center gap-2 px-3 sm:px-5">
          <button class="inline-flex h-10 w-10 items-center justify-center rounded-sm text-white hover:bg-white/10 lg:hidden" id="nr01-nav-open" type="button" aria-label="Abrir menu">
            ${icon('menu', 'h-5 w-5')}
          </button>

          ${sidebar_available ? '' : `
            <a class="inline-flex items-center gap-2 no-underline hover:no-underline" href="/">
              ${brand_mark_html()}
              <span class="hidden truncate text-sm font-semibold text-white sm:block">${PRODUCT.brand}</span>
            </a>
          `}

          <div class="flex min-w-0 flex-1 items-center gap-2">
            <span class="truncate text-[15px] font-semibold text-white" id="nr01-topbar-title">${escape_html(opts.title)}</span>
            ${env_badge_html()}
          </div>

          <div class="flex shrink-0 items-center gap-1.5">
            ${global_search_html()}
            ${appearance_controls_html(pref, ui_density)}
            <div id="nr01-user-slot">
              ${user_slot_html({
                label: 'Conta',
                email: '',
                meta: 'Não autenticado',
                authenticated: false,
                topbar: true
              })}
            </div>
          </div>
        </header>
        ${mfa_banner_html()}

        <div class="nr01-shell-content flex-1">
          <main id="main" class="nr-enter">
            ${page_content_html({
              title: opts.title,
              subtitle: opts.subtitle,
              header_actions_html: opts.header_actions_html,
              content_html: opts.content_html
            })}
          </main>
        </div>
      </div>

      <div class="fixed inset-0 z-50 hidden lg:hidden" id="nr01-drawer">
        <button class="absolute inset-0 bg-black/40" id="nr01-nav-close" aria-label="Fechar menu"></button>
        <aside class="absolute left-0 top-0 flex h-full w-[280px] max-w-[92vw] flex-col border-r border-border bg-surface">
          <div class="flex h-14 items-center justify-between gap-2 border-b border-border px-3">
            <a class="inline-flex items-center gap-2 no-underline hover:no-underline" href="/">
              ${brand_mark_html('surface')}
              <span class="text-sm font-semibold text-ink">${PRODUCT.brand}</span>
            </a>
            <button class="inline-flex h-9 w-9 items-center justify-center rounded-sm text-muted hover:bg-surface2 hover:text-ink" id="nr01-nav-close-btn" type="button" aria-label="Fechar menu">
              ${icon('close', 'h-5 w-5')}
            </button>
          </div>
          <div class="border-b border-border px-3 py-3">
            <div class="mb-2 text-[10px] font-bold uppercase tracking-wide text-muted">Aparência</div>
            <div class="flex gap-2">
              ${appearance_cycle_button_html('theme', pref, 'drawer')}
              ${appearance_cycle_button_html('density', ui_density, 'drawer')}
            </div>
          </div>
          <nav class="flex-1 overflow-y-auto pb-3 pt-2">
            ${sidebar_nav_html(opts.active_path)}
          </nav>
        </aside>
      </div>
    </div>
  `
}

function role_label_pt(role: string): string {
  const r = String(role || 'user').trim().toLowerCase()
  if (r === 'root') return 'Super Administrador'
  if (r === 'admin') return 'Administrador'
  if (r === 'technician') return 'Técnico'
  return 'Usuário'
}

function paint_appearance_controls(
  theme: ThemePreference = get_theme_preference(),
  density: UiDensityPreference = get_ui_density_preference()
): void {
  const theme_label = theme_preference_label(theme)
  const density_label = ui_density_preference_label(density)

  document.querySelectorAll<HTMLButtonElement>('[data-appearance-cycle="theme"]').forEach(btn => {
    btn.title = `Tema: ${theme_label} (clique para alternar)`
    btn.setAttribute('aria-label', `Alternar tema. Atual: ${theme_label}`)
    const variant = String(btn.getAttribute('data-appearance-variant') || 'menu')
    btn.innerHTML = variant === 'topbar'
      ? icon(theme_preference_icon(theme), 'h-4 w-4')
      : `${icon(theme_preference_icon(theme))} Tema: <span class="font-medium" data-appearance-label="theme">${escape_html(theme_label)}</span>`
  })

  document.querySelectorAll<HTMLButtonElement>('[data-appearance-cycle="density"]').forEach(btn => {
    btn.title = `Layout: ${density_label} (clique para alternar)`
    btn.setAttribute('aria-label', `Alternar layout. Atual: ${density_label}`)
    const variant = String(btn.getAttribute('data-appearance-variant') || 'menu')
    btn.innerHTML = variant === 'topbar'
      ? icon(ui_density_preference_icon(density), 'h-4 w-4')
      : `${icon(ui_density_preference_icon(density))} Layout: <span class="font-medium" data-appearance-label="density">${escape_html(density_label)}</span>`
  })
}

async function persist_ui_appearance_preferences(preferences: UiAppearancePreferences): Promise<void> {
  if (!shell_menu_auth_user) return
  const seq = ++shell_appearance_persist_seq
  try {
    await api_json<UiAppearancePreferences>('/api/auth/ui-appearance-preferences', {
      method: 'POST',
      body: JSON.stringify(preferences)
    })
  } catch {
    // localStorage já reflete a escolha; perfil é best-effort
  }
  if (seq !== shell_appearance_persist_seq) return
}

async function apply_appearance_cycle(kind: 'theme' | 'density'): Promise<void> {
  let theme = get_theme_preference()
  let density = get_ui_density_preference()

  if (kind === 'theme') {
    theme = next_theme_preference(theme)
    set_theme_preference(theme)
  } else {
    density = next_ui_density_preference(density)
    set_ui_density_preference(density)
  }

  paint_appearance_controls(theme, density)
  await persist_ui_appearance_preferences({ theme, ui_density: density })
}

function ensure_shell_appearance_listener(): void {
  if (shell_appearance_listener_registered) return
  document.addEventListener('click', event => {
    const button = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>('[data-appearance-cycle]')
    if (!button) return
    event.preventDefault()
    const kind = String(button.getAttribute('data-appearance-cycle') || '').trim()
    if (kind !== 'theme' && kind !== 'density') return
    void apply_appearance_cycle(kind)
  })
  shell_appearance_listener_registered = true
}

function normalize_search_text(value: string): string {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
}

function close_global_search_results(): void {
  document.querySelectorAll<HTMLElement>('[data-global-search-results]').forEach(panel => {
    panel.classList.add('hidden')
    panel.setAttribute('hidden', '')
    panel.innerHTML = ''
  })
  document.querySelectorAll<HTMLInputElement>('[data-global-search]').forEach(input => {
    input.setAttribute('aria-expanded', 'false')
  })
}

function render_global_search_results(query: string): void {
  const normalized = normalize_search_text(query)
  const panels = document.querySelectorAll<HTMLElement>('[data-global-search-results]')
  const inputs = document.querySelectorAll<HTMLInputElement>('[data-global-search]')

  if (!normalized) {
    close_global_search_results()
    return
  }

  const matches = all_nav_items()
    .filter(item => nav_item_allowed_for_user(item, shell_menu_auth_user))
    .filter(item => {
      const blob = normalize_search_text(`${item.label} ${item.description || ''} ${item.href}`)
      return blob.includes(normalized)
    })
    .slice(0, 12)

  const html = matches.length
    ? matches.map(item => `
        <a
          class="flex items-start gap-2 border-b border-border px-3 py-2.5 text-sm text-ink no-underline hover:no-underline last:border-b-0 hover:bg-surface2"
          href="${escape_html(item.href)}"
          role="option"
          data-global-search-result
        >
          <span class="mt-0.5 shrink-0 text-muted">${icon(item.icon, 'h-4 w-4')}</span>
          <span class="min-w-0">
            <span class="block font-medium leading-tight">${escape_html(item.label)}</span>
            ${item.description ? `<span class="block truncate text-xs text-muted">${escape_html(item.description)}</span>` : ''}
          </span>
        </a>
      `).join('')
    : `<div class="px-3 py-3 text-sm text-muted">Nenhum resultado para “${escape_html(query)}”.</div>`

  panels.forEach(panel => {
    panel.innerHTML = html
    panel.classList.remove('hidden')
    panel.removeAttribute('hidden')
  })
  inputs.forEach(input => input.setAttribute('aria-expanded', 'true'))
}

function ensure_shell_global_search_listener(): void {
  if (shell_global_search_listener_registered) return

  document.addEventListener('input', event => {
    const input = (event.target as HTMLElement | null)?.closest<HTMLInputElement>('[data-global-search]')
    if (!input) return
    render_global_search_results(input.value)
  })

  document.addEventListener('keydown', event => {
    const input = (event.target as HTMLElement | null)?.closest<HTMLInputElement>('[data-global-search]')
    if (!input) return
    if (event.key === 'Enter') {
      event.preventDefault()
      const first = document.querySelector<HTMLAnchorElement>('[data-global-search-result]')
      if (first) {
        close_global_search_results()
        first.click()
      }
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      input.value = ''
      close_global_search_results()
      input.blur()
    }
  })

  document.addEventListener('click', event => {
    const target = event.target as HTMLElement | null
    if (!target) return
    if (target.closest('[data-global-search-host]')) {
      if (target.closest('[data-global-search-result]')) close_global_search_results()
      return
    }
    close_global_search_results()
  })

  shell_global_search_listener_registered = true
}

async function load_ui_appearance_preferences(): Promise<UiAppearancePreferences | null> {
  try {
    const raw = await api_json<{ theme?: string | null; ui_density?: string | null }>(
      '/api/auth/ui-appearance-preferences'
    )
    const theme = String(raw?.theme || '').trim().toLowerCase()
    const ui_density = String(raw?.ui_density || '').trim().toLowerCase()
    if (
      (theme !== 'auto' && theme !== 'light' && theme !== 'dark')
      || (ui_density !== 'compact' && ui_density !== 'normal' && ui_density !== 'comfortable')
    ) {
      return null
    }
    return { theme, ui_density }
  } catch {
    return null
  }
}

function paint_mfa_banner(me: {
  mfa_state?: string
  mfa?: {
    strong?: boolean
    grace_until?: string | null
    grace_expired?: boolean
  }
}): void {
  const banner = document.getElementById('nr01-mfa-banner')
  const text = document.getElementById('nr01-mfa-banner-text')
  const cta = document.getElementById('nr01-mfa-banner-cta')
  if (!banner || !text) return

  const mfa = me.mfa || {}
  const state = String(me.mfa_state || 'satisfied')

  if (mfa.strong) {
    banner.classList.add('hidden')
    return
  }

  if (state === 'pending_setup') {
    banner.classList.remove('hidden')
    text.textContent = 'O prazo para ativar a verificação em 2 etapas terminou. Configure agora para continuar usando o sistema.'
    if (cta) {
      cta.textContent = 'Configurar agora'
      cta.setAttribute('href', '/account?tab=seguranca')
    }
    return
  }

  if (!mfa.grace_expired) {
    banner.classList.remove('hidden')
    const until = mfa.grace_until ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(new Date(mfa.grace_until)) : null
    text.textContent = until
      ? `Sua conta ainda não tem autenticação em 2 fatores. Ative até ${until} para manter o acesso sem interrupções.`
      : 'Sua conta ainda não tem autenticação em 2 fatores. Recomendamos ativar em até 30 dias.'
    if (cta) cta.classList.remove('hidden')
    return
  }

  banner.classList.add('hidden')
}

type ShellOrg = {
  id: number
  name: string
  slug?: string
  status?: string
  plan?: string
  membership_role?: string
}

type ShellMeResponse = {
  user?: {
    email?: string
    name?: string | null
    role?: string
    avatar_url?: string | null
    organization_id?: number | null
    acl_tags?: string[]
    entitlements?: string[]
    library_entitlements?: string[]
    is_platform_root?: boolean
    organization?: { id?: number; name?: string | null } | null
    organizations?: ShellOrg[]
  }
  mfa_state?: string
  mfa?: {
    strong?: boolean
    grace_until?: string | null
    grace_expired?: boolean
  }
}

let shell_app_version = ''
let shell_me_cache: ShellMeResponse | null = null
let systems_panel_tab: 'active' | 'available' = 'active'

function close_profile_menu(): void {
  const profileBtn = document.getElementById('nr01-profile-btn')
  const profileMenu = document.getElementById('nr01-profile-menu')
  profileMenu?.classList.add('hidden')
  profileBtn?.setAttribute('aria-expanded', 'false')
}

function open_profile_menu(): void {
  const profileBtn = document.getElementById('nr01-profile-btn')
  const profileMenu = document.getElementById('nr01-profile-menu')
  profileMenu?.classList.remove('hidden')
  profileBtn?.setAttribute('aria-expanded', 'true')
}

function bind_profile_menu(): void {
  const profileBtn = document.getElementById('nr01-profile-btn')
  const profileMenu = document.getElementById('nr01-profile-menu')
  if (!profileBtn || !profileMenu || profileBtn.dataset.bound === '1') return
  profileBtn.dataset.bound = '1'
  profileBtn.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (profileMenu.classList.contains('hidden')) open_profile_menu()
    else close_profile_menu()
  })
}

function bind_shell_logout(): void {
  document.getElementById('nr01-logout')?.addEventListener('click', async () => {
    try {
      await api_json('/api/auth/logout', { method: 'POST' })
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 401)) {
        // ignore expected guest state
      }
    }
    window.location.href = '/'
  })
}

function paint_shell_user(me: ShellMeResponse): boolean {
  const userSlot = document.getElementById('nr01-user-slot')
  if (!userSlot) return false

  const user = me?.user ?? null
  if (!user) return false

  shell_me_cache = me
  const email = String(user.email || '').trim()
  const role = String(user.role || 'user').trim().toLowerCase() || 'user'
  const label = String(user.name || '').trim() || email || 'Conta'
  const active_org_id = Number(user.organization_id || user.organization?.id || 0)
  const orgs = (user.organizations || []).map((o) => ({
    id: Number(o.id),
    name: String(o.name || `Org #${o.id}`),
    active: Number(o.id) === active_org_id
  }))
  const auth_user: ShellAuthUser = {
    email: user.email,
    name: user.name,
    role: user.role,
    avatar_url: user.avatar_url,
    acl_tags: user.acl_tags,
    entitlements: user.entitlements,
    library_entitlements: user.library_entitlements,
    is_platform_root: user.is_platform_root === true
  }

  userSlot.innerHTML = user_slot_html({
    label,
    email,
    meta: role_label_pt(role),
    avatar_url: user.avatar_url || null,
    authenticated: true,
    topbar: true,
    show_module_map: user_can_see_module_map(auth_user),
    app_version: shell_app_version || null,
    organizations: orgs
  })

  bind_profile_menu()
  bind_shell_logout()
  bind_org_switch_buttons()
  paint_systems_quick_bar(me)
  paint_mfa_banner(me)
  paint_appearance_controls()
  return true
}

type MotherSystemApi = {
  id: string
  brand: string
  chip: string
  tagline: string
  url: string
  description: string
  current: boolean
  accessible: boolean
}

let mother_systems_cache: MotherSystemApi[] | null = null

async function load_mother_systems(): Promise<MotherSystemApi[]> {
  if (mother_systems_cache) return mother_systems_cache
  try {
    const data = await api_json<{ systems: MotherSystemApi[] }>('/api/organization/systems')
    mother_systems_cache = data.systems || []
    return mother_systems_cache
  } catch {
    // fallback: só o produto do domínio
    return MOTHER_SYSTEMS.map((s) => ({
      id: s.id,
      brand: s.brand,
      chip: s.chip,
      tagline: s.tagline,
      url: s.url,
      description: s.tagline,
      current: s.id === PRODUCT.id,
      accessible: s.id === PRODUCT.id
    }))
  }
}

function paint_systems_quick_bar(_me: ShellMeResponse): void {
  const host = document.getElementById('nr01-systems-quick')
  if (!host) return
  // chips curtos: NR-01 / SGQ apenas
  void load_mother_systems().then((systems) => {
    const chips = systems.map((s) => {
      const active = s.current
      const cls = active
        ? 'border-white/50 bg-white text-[rgb(22,22,22)]'
        : s.accessible
          ? 'border-white/30 bg-white/15 text-white hover:bg-white/25'
          : 'border-white/20 bg-white/5 text-white/50'
      const title = active
        ? `${s.brand} — sistema atual`
        : s.accessible
          ? `Abrir ${s.brand}`
          : `${s.brand} — solicitar ativação`
      if (s.accessible && !active) {
        return `<a href="${escape_html(s.url)}" class="shrink-0 rounded-sm border px-2.5 py-1 text-[11px] font-bold tracking-wide no-underline hover:no-underline ${cls}" title="${escape_html(title)}">${escape_html(s.chip)}</a>`
      }
      if (!s.accessible) {
        return `<button type="button" class="shrink-0 rounded-sm border px-2.5 py-1 text-[11px] font-bold tracking-wide ${cls}"
          data-systems-open data-systems-open-tab="available" title="${escape_html(title)}">${escape_html(s.chip)}</button>`
      }
      return `<button type="button" class="shrink-0 rounded-sm border px-2.5 py-1 text-[11px] font-bold tracking-wide ${cls}"
        data-systems-open title="${escape_html(title)}">${escape_html(s.chip)}</button>`
    })
    host.innerHTML = chips.join('')
    bind_systems_panel_triggers()
  })
  bind_systems_panel_triggers()
}

function bind_org_switch_buttons(): void {
  // legado: troca de org permanece no menu do perfil se houver várias orgs
  document.querySelectorAll<HTMLElement>('[data-switch-org]').forEach((btn) => {
    if (btn.dataset.bound === '1') return
    btn.dataset.bound = '1'
    btn.addEventListener('click', async (e) => {
      e.preventDefault()
      e.stopPropagation()
      const id = Number(btn.getAttribute('data-switch-org') || 0)
      if (!id) return
      try {
        await api_json('/api/auth/switch-organization', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ organization_id: id })
        })
        window.location.reload()
      } catch (err) {
        const msg =
          err instanceof ApiError
            ? String((err.body as { error?: { message?: string } })?.error?.message || 'Falha ao trocar organização')
            : 'Falha ao trocar organização'
        window.alert(msg)
      }
    })
  })
}

function close_systems_panel(): void {
  const panel = document.getElementById('nr01-systems-panel')
  if (!panel) return
  panel.classList.add('hidden')
  panel.setAttribute('aria-hidden', 'true')
}

function open_systems_panel(tab?: 'active' | 'available'): void {
  const panel = document.getElementById('nr01-systems-panel')
  if (!panel) return
  if (tab) systems_panel_tab = tab
  panel.classList.remove('hidden')
  panel.setAttribute('aria-hidden', 'false')
  void paint_systems_panel_body()
}

function bind_systems_panel_triggers(): void {
  document.querySelectorAll<HTMLElement>('[data-systems-open]').forEach((el) => {
    if (el.dataset.boundOpen === '1') return
    el.dataset.boundOpen = '1'
    el.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      const t = el.getAttribute('data-systems-open-tab')
      open_systems_panel(t === 'available' ? 'available' : 'active')
    })
  })
  document.querySelectorAll<HTMLElement>('[data-systems-panel-close]').forEach((el) => {
    if (el.dataset.boundClose === '1') return
    el.dataset.boundClose = '1'
    el.addEventListener('click', () => close_systems_panel())
  })
  document.querySelectorAll<HTMLElement>('[data-systems-tab]').forEach((el) => {
    if (el.dataset.boundTab === '1') return
    el.dataset.boundTab = '1'
    el.addEventListener('click', () => {
      const t = el.getAttribute('data-systems-tab')
      systems_panel_tab = t === 'available' ? 'available' : 'active'
      document.querySelectorAll<HTMLElement>('[data-systems-tab]').forEach((tab) => {
        const on = tab.getAttribute('data-systems-tab') === systems_panel_tab
        tab.setAttribute('aria-selected', on ? 'true' : 'false')
        tab.className = on
          ? 'flex-1 rounded-sm px-2 py-1.5 text-xs font-semibold text-ink bg-surface shadow-sm'
          : 'flex-1 rounded-sm px-2 py-1.5 text-xs font-semibold text-muted hover:text-ink'
      })
      void paint_systems_panel_body()
    })
  })
}

async function paint_systems_panel_body(): Promise<void> {
  const body = document.querySelector<HTMLElement>('[data-systems-panel-body]')
  if (!body) return
  body.innerHTML = `<p class="text-sm text-muted">Carregando…</p>`

  try {
    mother_systems_cache = null
    const sys = await api_json<{
      systems: MotherSystemApi[]
      active: MotherSystemApi[]
      available: MotherSystemApi[]
      my_requests: Array<{ item_type: string; item_code: string; status: string }>
      pending_for_owner: Array<{
        id: number
        item_type: string
        item_code: string
        item_label: string | null
        requester_email?: string
        requester_name?: string
      }>
      is_manager: boolean
    }>('/api/organization/systems')
    mother_systems_cache = sys.systems || []

    const pending_codes = new Set(
      (sys.my_requests || [])
        .filter((r) => r.status === 'pending' && r.item_type === 'product')
        .map((r) => r.item_code)
    )

    let owner_block = ''
    if (sys.is_manager && (sys.pending_for_owner || []).length) {
      owner_block = `
        <div class="mb-3 rounded-sm border border-warn/40 bg-warn/10 p-3">
          <p class="text-xs font-semibold text-ink">Pedidos de sistema aguardando você</p>
          <ul class="mt-2 space-y-2">
            ${(sys.pending_for_owner || [])
              .map(
                (p) => `<li class="flex items-center justify-between gap-2 text-sm">
                  <span class="min-w-0 truncate">
                    <span class="font-medium text-ink">${escape_html(p.item_label || p.item_code)}</span>
                    <span class="block text-[11px] text-muted">${escape_html(p.requester_name || p.requester_email || '')}</span>
                  </span>
                  <span class="flex shrink-0 gap-1">
                    <button type="button" class="rounded-sm bg-action px-2 py-1 text-[11px] font-semibold text-action-ink" data-resolve-req="${p.id}" data-approve="1">Ativar</button>
                    <button type="button" class="rounded-sm border border-border px-2 py-1 text-[11px] font-semibold text-ink" data-resolve-req="${p.id}" data-approve="0">Recusar</button>
                  </span>
                </li>`
              )
              .join('')}
          </ul>
        </div>
      `
    }

    if (systems_panel_tab === 'active') {
      const list = sys.active || []
      body.innerHTML = `
        ${owner_block}
        <p class="mb-2 text-xs text-muted">Apps principais que você já pode usar. Nomes curtos: <strong class="text-ink">NR-01</strong> e <strong class="text-ink">SGQ</strong>.</p>
        <ul class="space-y-1.5">
          ${list
            .map((s) => {
              const is_current = s.current
              const inner = `
                <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-border bg-surface2 text-xs font-bold text-ink">${escape_html(s.chip)}</span>
                <span class="min-w-0 flex-1">
                  <span class="block text-sm font-semibold text-ink">${escape_html(s.brand)}</span>
                  <span class="block text-[11px] text-muted">${escape_html(s.tagline)}</span>
                </span>
                ${is_current ? '<span class="text-[10px] font-bold uppercase text-muted">atual</span>' : '<span class="text-[10px] text-muted">abrir</span>'}
              `
              if (is_current) {
                return `<li><div class="flex w-full items-center gap-3 rounded-sm border border-border bg-surface2 px-3 py-2">${inner}</div></li>`
              }
              return `<li><a href="${escape_html(s.url)}" class="flex w-full items-center gap-3 rounded-sm border border-border bg-surface px-3 py-2 text-ink no-underline hover:no-underline hover:bg-surface2">${inner}</a></li>`
            })
            .join('')}
        </ul>
      `
      return
    }

    // disponíveis — só apps-mãe sem acesso
    const list = sys.available || []
    body.innerHTML = `
      ${owner_block}
      <p class="mb-2 text-xs text-muted">Solicite o app principal. O dono da organização ativa sob demanda (trial ou produção).</p>
      ${
        list.length
          ? `<ul class="space-y-1.5">
        ${list
          .map((s) => {
            const pending = pending_codes.has(s.id)
            return `<li class="flex items-center justify-between gap-2 rounded-sm border border-border px-3 py-2">
              <span class="flex min-w-0 items-center gap-3">
                <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-border bg-surface2 text-xs font-bold text-ink">${escape_html(s.chip)}</span>
                <span class="min-w-0">
                  <span class="block text-sm font-semibold text-ink">${escape_html(s.brand)}</span>
                  <span class="block truncate text-[11px] text-muted">${escape_html(s.description)}</span>
                </span>
              </span>
              <button type="button"
                class="shrink-0 rounded-sm border border-border px-2 py-1 text-[11px] font-semibold ${
                  pending ? 'bg-surface2 text-muted' : 'bg-action text-action-ink'
                }"
                data-request-system="product:${escape_html(s.id)}"
                data-label="${escape_html(s.brand)}"
                ${pending ? 'disabled' : ''}
              >${pending ? 'Pendente' : 'Solicitar'}</button>
            </li>`
          })
          .join('')}
      </ul>`
          : `<p class="text-sm text-muted">Você já tem acesso aos sistemas principais disponíveis.</p>`
      }
    `

    body.querySelectorAll<HTMLElement>('[data-request-system]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const raw = btn.getAttribute('data-request-system') || ''
        const [item_type, item_code] = raw.split(':')
        const item_label = btn.getAttribute('data-label') || item_code
        try {
          await api_json('/api/organization/systems/request', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ item_type, item_code, item_label })
          })
          mother_systems_cache = null
          btn.textContent = 'Pendente'
          btn.setAttribute('disabled', 'true')
        } catch (err) {
          const msg =
            err instanceof ApiError
              ? String((err.body as { error?: { message?: string } })?.error?.message || 'Falha')
              : 'Falha ao solicitar'
          window.alert(msg)
        }
      })
    })

    body.querySelectorAll<HTMLElement>('[data-resolve-req]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = Number(btn.getAttribute('data-resolve-req') || 0)
        const approve = btn.getAttribute('data-approve') === '1'
        try {
          await api_json(`/api/organization/systems/requests/${id}/resolve`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ approve })
          })
          mother_systems_cache = null
          void paint_systems_panel_body()
        } catch (err) {
          const msg =
            err instanceof ApiError
              ? String((err.body as { error?: { message?: string } })?.error?.message || 'Falha')
              : 'Falha ao resolver'
          window.alert(msg)
        }
      })
    })
  } catch {
    body.innerHTML = `<p class="text-sm text-muted">Não foi possível carregar os sistemas. Tente de novo.</p>`
  }
}

export async function refresh_shell_user(): Promise<void> {
  try {
    const me = await api_json<ShellMeResponse>('/api/auth/me')
    paint_shell_user(me)
  } catch {
    // keep current topbar state
  }
}

function format_nav_stat_value(value: number): string {
  const count = Math.max(0, Math.trunc(Number(value || 0)))
  if (count > 9999) return '9k+'
  if (count > 999) return `${Math.floor(count / 1000)}k`
  return String(count)
}

function nav_stat_badge_html(
  kind: 'count' | 'alert' | 'error',
  value: number,
  hint?: string | null
): string {
  const count = Math.max(0, Math.trunc(Number(value || 0)))
  if (count <= 0) return ''

  const label = kind === 'count' ? 'Total' : kind === 'alert' ? 'Atenção' : 'Crítico'
  const tone = kind === 'count'
    ? 'border border-border bg-surface2 text-ink'
    : kind === 'alert'
      ? 'border border-warn/50 bg-warn/10 text-ink'
      : 'border border-danger/50 bg-danger/10 text-ink'

  const tip = String(hint || '').trim() || `${label}: ${count.toLocaleString('pt-BR')}`
  const safe_tip = escape_html(tip)

  return `
    <span
      class="inline-flex h-5 min-w-5 shrink-0 cursor-help items-center justify-center rounded-sm px-1.5 text-[10px] font-bold leading-none ${tone}"
      title="${safe_tip}"
      aria-label="${safe_tip}"
      data-nav-stat-kind="${kind}"
    >${format_nav_stat_value(count)}</span>
  `
}

function nav_stats_badges_html(stat: MenuStatsItem): string {
  // Neutro = volume · âmbar = atenção · vermelho = crítico/vencido
  const details = stat.details || {}
  return [
    nav_stat_badge_html('count', stat.record_count, details.record_hint),
    nav_stat_badge_html('alert', stat.alert_count, details.alert_hint),
    nav_stat_badge_html('error', stat.error_count, details.error_hint)
  ].join('')
}

function paint_menu_stats(bundle: MenuStatsBundle): void {
  menu_stats_last_bundle = bundle
  const stats_by_href = new Map<string, MenuStatsItem>()
  for (const item of bundle.items || []) {
    if (!item?.href) continue
    stats_by_href.set(item.href, item)
  }

  document.querySelectorAll<HTMLElement>('[data-nav-stats-href]').forEach(anchor => {
    const href = String(anchor.getAttribute('data-nav-stats-href') || '')
    const host = anchor.querySelector<HTMLElement>('[data-nav-stats-badges]')
    if (!host) return

    const stat = stats_by_href.get(href)
    const html = stat ? nav_stats_badges_html(stat) : ''
    const alert_count = stat ? Math.max(0, Math.trunc(Number(stat.alert_count || 0))) : 0
    const error_count = stat ? Math.max(0, Math.trunc(Number(stat.error_count || 0))) : 0
    const attention = error_count > 0 ? 'error' : alert_count > 0 ? 'alert' : 'none'
    const hide_clean_onboarding = href === '/onboarding' && Boolean(stat) && alert_count + error_count === 0
    const entry = anchor.closest<HTMLElement>('[data-nav-entry]')
    entry?.classList.toggle('hidden', hide_clean_onboarding)
    entry?.setAttribute('aria-hidden', hide_clean_onboarding ? 'true' : 'false')
    entry?.setAttribute('data-nav-entry-hidden-by-stats', hide_clean_onboarding ? '1' : '0')
    anchor.setAttribute('data-nav-attention', attention)
    anchor.setAttribute('data-nav-stat-total', String(alert_count + error_count))
    host.innerHTML = html
    host.classList.toggle('hidden', !html)
    host.classList.toggle('inline-flex', Boolean(html))
  })
  apply_nav_search_filter(shell_menu_search_query)
}

export async function refresh_shell_menu_stats(): Promise<void> {
  if (menu_stats_inflight) return
  menu_stats_inflight = true
  try {
    const bundle = await api_json<MenuStatsBundle>('/api/menu-stats')
    paint_menu_stats(bundle)
  } catch {
    // badges são metadados best-effort
  } finally {
    menu_stats_inflight = false
  }
}

function start_menu_stats_polling(): void {
  if (menu_stats_timer !== null) return
  void refresh_shell_menu_stats()
  menu_stats_timer = window.setInterval(() => {
    if (!document.querySelector('[data-nav-stats-href]')) return
    void refresh_shell_menu_stats()
  }, 10_000)
}

export async function init_shell(): Promise<void> {
  ensure_shell_appearance_listener()
  ensure_shell_global_search_listener()
  paint_appearance_controls()
  if (menu_stats_last_bundle) paint_menu_stats(menu_stats_last_bundle)

  const openBtn = document.getElementById('nr01-nav-open')
  const closeBtn = document.getElementById('nr01-nav-close')
  const closeBtn2 = document.getElementById('nr01-nav-close-btn')

  function close_drawer() {
    document.getElementById('nr01-drawer')?.classList.add('hidden')
  }

  function open_drawer() {
    document.getElementById('nr01-drawer')?.classList.remove('hidden')
  }

  // Bind por elemento (HTML novo a cada mount); listeners de document/window ficam once.
  openBtn?.addEventListener('click', () => open_drawer())
  closeBtn?.addEventListener('click', () => close_drawer())
  closeBtn2?.addEventListener('click', () => close_drawer())

  bind_profile_menu()
  bind_systems_panel_triggers()

  if (!shell_document_listeners_registered) {
    window.addEventListener('nr01:theme', (e: Event) => {
      const pref = String((e as CustomEvent<{ preference?: string }>).detail?.preference || '').trim().toLowerCase() as ThemePreference
      if (pref === 'auto' || pref === 'light' || pref === 'dark') paint_appearance_controls(pref, get_ui_density_preference())
    })

    window.addEventListener('nr01:ui-density', (e: Event) => {
      const pref = String((e as CustomEvent<{ preference?: string }>).detail?.preference || '').trim().toLowerCase() as UiDensityPreference
      if (pref === 'compact' || pref === 'normal' || pref === 'comfortable') {
        paint_appearance_controls(get_theme_preference(), pref)
      }
    })

    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement | null
      if (!target) return
      if (target.closest('#nr01-profile-btn') || target.closest('#nr01-profile-menu')) return
      close_profile_menu()
    })

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        close_drawer()
        close_profile_menu()
        close_systems_panel()
        close_global_search_results()
      }
    })

    shell_document_listeners_registered = true
  }

  const envMode = document.getElementById('nr01-env-mode')

  try {
    const h = await api_json<{ mode?: string }>('/api/health')
    const mode = String(h?.mode || '').trim() || 'n/a'
    if (envMode) envMode.textContent = environment_badge_label(mode)
  } catch {
    if (envMode) envMode.textContent = 'n/a'
  }

  try {
    const v = await api_json<{ version?: string; git_hash?: string }>('/api/version')
    shell_app_version = v && v.version ? `${v.version}.${v.git_hash || ''}`.replace(/\.$/, '') : 'dev'
  } catch {
    shell_app_version = 'dev'
  }

  ensure_shell_menu_preferences_listener()
  ensure_shell_menu_search_listener()

  try {
    const me = await api_json<ShellMeResponse>('/api/auth/me')
    const user = me?.user ?? null
    if (!user || !paint_shell_user(me)) {
      shell_menu_auth_user = null
      apply_shell_menu_preferences(null, DEFAULT_SHELL_MENU_PREFERENCES)
      return
    }

    shell_menu_auth_user = {
      email: user.email,
      name: user.name,
      role: user.role,
      avatar_url: user.avatar_url,
      acl_tags: user.acl_tags,
      entitlements: user.entitlements,
      library_entitlements: user.library_entitlements,
      is_platform_root: user.is_platform_root === true
    }

    const role = String(user.role || 'user').trim().toLowerCase() || 'user'
    const acl_tags: string[] = Array.isArray(user.acl_tags) ? user.acl_tags : []
    const has_wildcard = acl_tags.includes('*')
    const is_root = role === 'root'
    const tag_set = new Set(acl_tags)

    if (!is_root && !has_wildcard) {
      document.querySelectorAll<HTMLElement>('[data-acl-tag]').forEach(el => {
        const required = el.getAttribute('data-acl-tag') || ''
        if (required && !tag_set.has(required)) {
          el.setAttribute('data-nav-acl-hidden', '1')
          el.style.display = 'none'
        }
      })
    }

    // Branding por organização: o subtítulo da marca vira o nome da org ativa.
    const organization_name = String(user.organization?.name || '').trim()
    if (organization_name) {
      document.querySelectorAll<HTMLElement>('[data-brand-org]').forEach(el => {
        el.textContent = organization_name
        el.setAttribute('title', organization_name)
      })
    }

    // Item de plataforma: nasce escondido no HTML e só aparece quando o /me
    // confirma is_platform_root (a decisão é do backend; aqui só reflete).
    document.querySelectorAll<HTMLElement>('[data-platform-admin]').forEach(el => {
      if (user.is_platform_root === true) {
        el.removeAttribute('data-nav-platform-hidden')
        el.style.display = ''
      } else {
        el.setAttribute('data-nav-platform-hidden', '1')
        el.style.display = 'none'
      }
    })

    // Entitlements da organização (F2 da fusão): módulo fora do plano some do
    // menu para TODOS — sem bypass de root/wildcard (a org não tem o módulo).
    const entitlement_list = Array.isArray(user.entitlements) ? user.entitlements : null
    if (entitlement_list) {
      const entitlement_set = new Set(entitlement_list)
      document.querySelectorAll<HTMLElement>('[data-module-code]').forEach(el => {
        const code = el.getAttribute('data-module-code') || ''
        if (code && !entitlement_set.has(code)) {
          el.setAttribute('data-nav-entitlement-hidden', '1')
          el.style.display = 'none'
        }
      })
    }

    // Bibliotecas (F5): painel de conformidade some se a org não tem library.
    const library_list = Array.isArray(user.library_entitlements) ? user.library_entitlements : null
    if (library_list) {
      document.querySelectorAll<HTMLElement>('[data-requires-library]').forEach(el => {
        if (library_list.length === 0) {
          el.setAttribute('data-nav-library-hidden', '1')
          el.style.display = 'none'
        } else {
          el.removeAttribute('data-nav-library-hidden')
          if (!el.hasAttribute('data-nav-entitlement-hidden') && !el.hasAttribute('data-nav-platform-hidden')) {
            el.style.display = ''
          }
        }
      })
    }

    sync_nav_group_visibility()
    const preferences = await load_shell_menu_preferences()
    apply_shell_menu_preferences(shell_menu_auth_user, preferences)

    const appearance = await load_ui_appearance_preferences()
    if (appearance) {
      if (appearance.theme === 'auto' || appearance.theme === 'light' || appearance.theme === 'dark') {
        set_theme_preference(appearance.theme)
      }
      if (
        appearance.ui_density === 'compact'
        || appearance.ui_density === 'normal'
        || appearance.ui_density === 'comfortable'
      ) {
        set_ui_density_preference(appearance.ui_density)
      }
      paint_appearance_controls(get_theme_preference(), get_ui_density_preference())
    }

    // Badges do menu: lê tabela materializada (sem COUNT por render)
    start_menu_stats_polling()
  } catch {
    shell_menu_auth_user = null
    apply_shell_menu_preferences(null, DEFAULT_SHELL_MENU_PREFERENCES)
  }
}
